import React, { useMemo } from 'react';

import { Card, Divider, Flex } from 'antd-v5';
import { groupBy } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Heading, Text } from 'scouting-ui/lib';

import type {
  MeritBadgeUserRequirements,
  RosterYouthMember,
} from '@appTypes/esb';
import { ConditionalRender } from '@shared/components';

import { selectedYouthByMIDSel, validationErrorsSel } from '../duck/selectors';
import { bulkAssignActions } from '../duck/slice';
import styles from './BulkMBCAssignPage.module.scss';
import { YouthItem } from './YouthItem';

interface YouthSelectionProps {
  youths: RosterYouthMember[];
  mbRequirementsByMid: Map<number, MeritBadgeUserRequirements>;
}
export default function YouthSelection(props: YouthSelectionProps) {
  const { youths, mbRequirementsByMid } = props;
  const dispatch = useDispatch();
  const errors = useSelector(validationErrorsSel);
  const selectedYouthByMID = useSelector(selectedYouthByMIDSel);

  const { eligible, notEligible } = useMemo(() => {
    const grouped = groupBy(youths || [], youth =>
      youth.memberId in errors ? 'invalid' : 'valid',
    );
    return {
      eligible: grouped.valid || [],
      notEligible: grouped.invalid || [],
    };
  }, [errors, youths]);

  return (
    <Card>
      <Flex vertical gap="small">
        <Heading font="display-h5">
          <FormattedMessage id="page.mbcBulkAssign.selectYouth" />
          <Divider className={styles.selectYouthDivider} />
        </Heading>
        {eligible.map(youth => (
          <YouthItem
            checked={selectedYouthByMID.has(youth.memberId)}
            onSelect={checked =>
              dispatch(bulkAssignActions.toggleYouth({ youth, checked }))
            }
            youth={youth}
            key={youth.userId}
          />
        ))}
        <ConditionalRender value={notEligible.length > 0}>
          <Divider className={styles.selectYouthDivider} />
          <Text font="body-overline">
            <FormattedMessage id="page.mbcBulkAssign.youths.notEligible" />
          </Text>
          {notEligible.map(youth => {
            const key = youth.memberId;

            return (
              <YouthItem
                checked={selectedYouthByMID.has(youth.memberId)}
                onSelect={checked =>
                  dispatch(bulkAssignActions.toggleYouth({ youth, checked }))
                }
                mbRequirements={mbRequirementsByMid.get(youth.memberId)}
                youth={youth}
                disabled
                key={key}
                errors={errors[youth.memberId]?.map(err => err.error)}
              />
            );
          })}
        </ConditionalRender>
      </Flex>
    </Card>
  );
}
